/**
 * NETLIFY WAVE (N2) — offline-first cloud saves over Netlify Identity + Blobs.
 *
 * localStorage remains the source of truth; the cloud is a mirror:
 *  - push(): debounced PUT /api/save piggybacked on every local persist.
 *  - pull(): boot-time GET; if the cloud record is NEWER than the local write,
 *    a sombre prompt offers the restore (newest-wins WITH player consent).
 * Fully optional — guests play local-only forever. Feature-gated to Netlify
 * hosts (or ?cloud=1 for `netlify dev`).
 */
import GoTrue, { type User } from 'gotrue-js';

const PUSH_DEBOUNCE_MS = 60_000;
const LOCAL_TS_KEY = 'blackmarble.save.localTs';

export class CloudSaves {
  readonly enabled: boolean;
  private readonly auth: GoTrue | null = null;
  private pushTimer: number | null = null;
  private pendingJson: string | null = null;
  /** One-line sync status for the RECORD KEEPER panel. */
  status = 'LOCAL RECORD ONLY';

  constructor() {
    const params = new URLSearchParams(window.location.search);
    this.enabled =
      window.location.hostname.endsWith('.netlify.app') || params.get('cloud') === '1';
    if (this.enabled) {
      this.auth = new GoTrue({
        APIUrl: `${window.location.origin}/.netlify/identity`,
        setCookie: false,
      });
      this.status = this.user ? `KEPT FOR ${this.user.email.toUpperCase()}` : 'NOT SIGNED IN';
    }
  }

  get user(): User | null {
    return this.auth?.currentUser() ?? null;
  }
  get signedIn(): boolean {
    return this.user !== null;
  }

  async signIn(email: string, password: string): Promise<string> {
    if (!this.auth) return 'CLOUD UNAVAILABLE';
    try {
      const u = await this.auth.login(email, password, true);
      this.status = `KEPT FOR ${u.email.toUpperCase()}`;
      return this.status;
    } catch (e) {
      return `SIGN-IN FAILED — ${(e as Error).message ?? 'unknown'}`.toUpperCase();
    }
  }

  async register(email: string, password: string): Promise<string> {
    if (!this.auth) return 'CLOUD UNAVAILABLE';
    try {
      await this.auth.signup(email, password);
      // Open-signup sites can log straight in; confirm-required sites get email.
      try {
        await this.auth.login(email, password, true);
        this.status = `KEPT FOR ${email.toUpperCase()}`;
        return this.status;
      } catch {
        return 'REGISTERED — CONFIRM VIA EMAIL, THEN SIGN IN';
      }
    } catch (e) {
      return `REGISTER FAILED — ${(e as Error).message ?? 'unknown'}`.toUpperCase();
    }
  }

  async signOut(): Promise<void> {
    await this.user?.logout().catch(() => undefined);
    this.status = 'NOT SIGNED IN';
  }

  /** Record the local write time (called by every local persist). */
  static stampLocal(): void {
    try {
      localStorage.setItem(LOCAL_TS_KEY, new Date().toISOString());
    } catch {
      /* storage unavailable */
    }
  }

  static localStamp(): string | null {
    return localStorage.getItem(LOCAL_TS_KEY);
  }

  /** Debounced cloud mirror of the local save (fire-and-forget). */
  push(saveJson: string): void {
    if (!this.enabled || !this.signedIn) return;
    this.pendingJson = saveJson;
    if (this.pushTimer !== null) return;
    this.pushTimer = window.setTimeout(() => {
      this.pushTimer = null;
      void this.flush();
    }, PUSH_DEBOUNCE_MS);
  }

  /** Immediate PUT (panel SYNC NOW + beforeunload). */
  async flush(): Promise<string> {
    if (!this.enabled || !this.signedIn || !this.pendingJson) return this.status;
    try {
      const token = await this.user!.jwt();
      const res = await fetch('/api/save', {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: this.pendingJson,
      });
      this.status = res.ok
        ? `RECORD KEPT — ${new Date().toISOString().slice(11, 16)}Z`
        : `KEEPER REFUSED (${res.status})`;
    } catch {
      this.status = 'KEEPER UNREACHABLE — LOCAL RECORD HOLDS';
    }
    return this.status;
  }

  /** Boot-time fetch of the kept record. Null = none / unavailable. */
  async pull(): Promise<{ save: unknown; updatedAt: string | null } | null> {
    if (!this.enabled || !this.signedIn) return null;
    try {
      const token = await this.user!.jwt();
      const res = await fetch('/api/save', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.status !== 200) return null;
      return (await res.json()) as { save: unknown; updatedAt: string | null };
    } catch {
      return null;
    }
  }
}

/** Boot-time prompt (sombre register): the kept record is newer — restore?
 * Returns true to restore. Renders its own minimal overlay. */
export const offerNewerRecord = (updatedAt: string): Promise<boolean> =>
  new Promise((resolve) => {
    const el = document.createElement('div');
    el.id = 'record-prompt';
    el.style.cssText =
      'position:fixed;inset:0;z-index:45;display:flex;align-items:center;justify-content:center;' +
      'background:rgba(0,0,0,0.92);font-family:var(--bm-mono);text-transform:uppercase;' +
      'color:#8aa3a8;text-shadow:none;user-select:none;';
    el.innerHTML =
      `<div style="text-align:center;border:1px solid #8aa3a8;padding:30px 48px;background:rgba(0,0,0,0.6)">` +
      `<div style="font-size:22px;letter-spacing:0.14em;color:#cfe9ee">A NEWER RECORD EXISTS</div>` +
      `<div style="margin-top:10px;font-size:12px">KEPT ${updatedAt.slice(0, 16).replace('T', ' ')}Z</div>` +
      `<div style="margin-top:24px;display:flex;gap:14px;justify-content:center">` +
      `<button data-r="1" style="font-family:var(--bm-mono);text-transform:uppercase;color:#3fd0e0;background:rgba(63,208,224,0.06);border:1px solid rgba(63,208,224,0.5);border-radius:0;padding:8px 14px;cursor:pointer">[1] RESTORE IT</button>` +
      `<button data-r="2" style="font-family:var(--bm-mono);text-transform:uppercase;color:#ffb000;background:rgba(255,176,0,0.06);border:1px solid rgba(255,176,0,0.5);border-radius:0;padding:8px 14px;cursor:pointer">[2] KEEP LOCAL</button>` +
      `</div></div>`;
    document.body.appendChild(el);
    const done = (restore: boolean): void => {
      el.remove();
      window.removeEventListener('keydown', onKey);
      resolve(restore);
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === '1') done(true);
      if (e.key === '2') done(false);
    };
    window.addEventListener('keydown', onKey);
    el.addEventListener('click', (e) => {
      const b = (e.target as HTMLElement).closest('[data-r]');
      if (b) done(b.getAttribute('data-r') === '1');
    });
  });
